import React from "react";

/**
 * Renders plain text with embedded URLs turned into clickable anchors.
 *
 * Used for description / notes / content fields in detail modals and inline
 * editable table cells. Inside an editable cell the surrounding wrapper has a
 * click-to-edit handler, so each anchor calls `stopPropagation()` on click and
 * keydown — clicking the link opens the link, clicking anywhere else in the
 * cell still opens the editor.
 *
 * Only `http(s)://` and bare `www.` URLs are linkified, and every candidate is
 * validated through the URL constructor (http/https only) so we never emit an
 * unsafe href.
 */

// Matches http(s):// URLs and bare www. URLs. Trailing punctuation that commonly
// hugs a URL in prose is trimmed off the match below.
const URL_PATTERN = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;

/** Strip trailing punctuation that often follows a URL in prose. */
function trimTrailingPunctuation(url: string): { url: string; trailing: string } {
  const match = url.match(/[.,;:!?'")\]>]+$/);
  if (!match) return { url, trailing: "" };
  const trailing = match[0];
  return { url: url.slice(0, url.length - trailing.length), trailing };
}

/** Build a safe http(s) href, or null if the candidate isn't a safe URL. */
function toSafeHref(candidate: string): string | null {
  const withProtocol = candidate.startsWith("www.") ? `https://${candidate}` : candidate;
  try {
    const parsed = new URL(withProtocol);
    if (parsed.protocol === "http:" || parsed.protocol === "https:") {
      return parsed.toString();
    }
  } catch {
    return null;
  }
  return null;
}

export interface LinkifiedTextProps {
  text: string;
  className?: string;
}

export function LinkifiedText({ text, className }: LinkifiedTextProps): React.ReactElement {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  for (const match of text.matchAll(URL_PATTERN)) {
    const matchStr = match[0];
    const offset = match.index ?? 0;

    const { url, trailing } = trimTrailingPunctuation(matchStr);
    const href = toSafeHref(url);

    // Text before the URL.
    if (offset > lastIndex) {
      nodes.push(<span key={`t-${key}`}>{text.slice(lastIndex, offset)}</span>);
      key += 1;
    }

    if (href) {
      nodes.push(
        <a
          key={`a-${key}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          // Inside a click-to-edit cell: keep the click on the link, not the cell.
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") e.stopPropagation();
          }}
          className="text-blue-600 underline underline-offset-2 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 break-all"
        >
          {url}
        </a>
      );
      key += 1;
      if (trailing) {
        nodes.push(<span key={`p-${key}`}>{trailing}</span>);
        key += 1;
      }
    } else {
      // Not a safe URL — render the raw match as text.
      nodes.push(<span key={`r-${key}`}>{matchStr}</span>);
      key += 1;
    }

    lastIndex = offset + matchStr.length;
  }

  // Trailing text after the last URL.
  if (lastIndex < text.length) {
    nodes.push(<span key={`t-${key}`}>{text.slice(lastIndex)}</span>);
  }

  return <span className={className}>{nodes}</span>;
}
